import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, Typography, message, Form, Input } from 'antd';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import {
  privacyPolicyActionStart,
  privacyPolicyActionError,
  getAllPrivacyPoliciesAction,
  createPrivacyPolicyAction,
  expirePrivacyPolicyAction,
  activatePrivacyPolicyAction,
} from '../../core/data/redux/modules/privacyPolicy/actions';
import { getAllPrivacyPolicies, createPrivacyPolicy, expirePrivacyPolicy, activatePrivacyPolicy } from '../../services/adminServices/privacyPolicy';
import RichTextEditor from '../common/components/RichTextEditor';
import { prepareContentForAPI } from '../../utils/richText';

const { Title } = Typography;

const ListPrivacyPolicies = () => {
  const dispatch = useDispatch();
  const { privacyPolicies, loading, error } = useSelector((state: any) => state.privacyPolicy || { privacyPolicies: [], loading: false, error: null });
  const [tableData, setTableData] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Rich text editor states
  const [policyContentJson, setPolicyContentJson] = useState<any>(null);
  const [policyHtml, setPolicyHtml] = useState<string>('');

  useEffect(() => {
    fetchPrivacyPolicies();
    // eslint-disable-next-line
  }, []);

  const fetchPrivacyPolicies = async () => {
    dispatch(privacyPolicyActionStart());
    try {
      const res = await getAllPrivacyPolicies();
      if (res.error) throw new Error(res.error || 'Error fetching privacy policies');
      const data = Array.isArray(res.data) ? res.data : [];
      
      // Sort by version in descending order (newest first)
      const sortedData = data.sort((a, b) => {
        // Convert version strings to numbers for proper comparison
        const versionA = parseFloat(a.version) || 0;
        const versionB = parseFloat(b.version) || 0;
        return versionB - versionA;
      });
      
      dispatch(getAllPrivacyPoliciesAction(sortedData));
      setTableData(sortedData);
    } catch (err: any) {
      dispatch(privacyPolicyActionError(err.message));
      message.error(err.message);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setPolicyContentJson(null);
    setPolicyHtml('');
    setIsModalVisible(true);
  };

  const handleExpire = async (id: string) => {
    setActionLoading(id);
    try {
      await expirePrivacyPolicy(id);
      dispatch(expirePrivacyPolicyAction(id));
      message.success('Privacy policy expired');
      fetchPrivacyPolicies();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (id: string) => {
    setActionLoading(id);
    try {
      await activatePrivacyPolicy(id);
      dispatch(activatePrivacyPolicyAction(id));
      message.success('Privacy policy activated');
      fetchPrivacyPolicies();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        version: values.version,
        privacy_policy_text: prepareContentForAPI(policyContentJson, policyHtml),
      };
      const res = await createPrivacyPolicy(payload);
      if (res.error) throw new Error(res.error);
      dispatch(createPrivacyPolicyAction(res.data));
      message.success('Privacy policy created');
      setIsModalVisible(false);
      fetchPrivacyPolicies();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => {
        const status = record.is_expired ? 'expired' : record.is_active ? 'active' : 'inactive';
        return (
          <Tag color={status === 'active' ? 'green' : status === 'expired' ? 'red' : 'orange'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Link to={`/admin/privacy-policy/privacy-policy-detail/${record.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          {!record.is_expired && (
            <Button icon={<Icon.StopCircle size={16} />} size="small" loading={actionLoading === record.id} onClick={() => handleExpire(record.id)}>
              Expire
            </Button>
          )}
          {!record.is_active && (
            <Button icon={<Icon.CheckCircle size={16} />} size="small" loading={actionLoading === record.id} onClick={() => handleActivate(record.id)}>
              Activate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/privacy-policy/list-privacy-policies">Privacy Policy</Link>
          </Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Button icon={<Icon.Plus size={16} />} type="primary" onClick={handleAdd}>
              Add Privacy Policy
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        <Modal
          title={'Add Privacy Policy'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
          okText={'Create'}
          width={800}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="version"
              label="Version"
              rules={[{ required: true, message: 'Please enter version' }]}
            >
              <Input placeholder="Enter version (e.g. 1.0.0)" />
            </Form.Item>
             <Form.Item
               label="Privacy Policy Text"
               rules={[{ required: true, message: 'Please enter privacy policy text' }]}
             >
               <RichTextEditor
                 value={policyHtml}
                 onChange={(htmlContent, jsonContent) => {
                   setPolicyHtml(htmlContent);
                   setPolicyContentJson(jsonContent);
                 }}
                 placeholder="Enter privacy policy text"
                 height={300}
               />
             </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ListPrivacyPolicies;

